import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { MapPin, Home, AlertCircle, X, ChevronLeft, ChevronRight } from 'lucide-react';
import api from '../api';
import ContactForm from '../components/ContactForm';
import Seo from '../components/Seo';
import { tipoLabel } from '../utils/tipi';
import { formatPrezzo } from '../utils/prezzo';
import { servizioPath, providerPath, idFromSlug } from '../utils/slug';
import './DettaglioServizio.css';

const fmtEuro = (n) => {
  const num = Number(n);
  return `€${num % 1 === 0 ? num : num.toFixed(2)}`;
};

// Prezzo di una voce, formattato in base alla modalità.
const prezzoVoce = (v) => {
  if (v.prezzo == null || v.prezzo === '') {
    const prezzi = (v.opzioni || []).map((o) => Number(o.prezzo)).filter((x) => !Number.isNaN(x));
    return prezzi.length ? `da ${fmtEuro(Math.min(...prezzi))}` : 'su richiesta';
  }
  const p = fmtEuro(v.prezzo);
  switch (v.prezzo_modalita) {
    case 'da': return `da ${p}`;
    case 'al_kg': return `${p}/kg`;
    case 'cad': return `${p} cad.`;
    case 'supplemento': return `+${p}`;
    default: return p;
  }
};

function VoceListinoView({ v }) {
  return (
    <div className="listino-voce">
      <div className="listino-voce-head">
        <strong>{v.titolo}</strong>
        <span className="listino-voce-prezzo">{prezzoVoce(v)}</span>
      </div>
      <div className="listino-voce-meta">
        {v.durata && <span className="listino-chip">⏱ {v.durata}</span>}
        {v.max_bambini != null && <span className="listino-chip">👧 max {v.max_bambini} bambini</span>}
        {v.prezzo_bambino_extra != null && (
          <span className="listino-chip">+{fmtEuro(v.prezzo_bambino_extra)} a bambino extra</span>
        )}
        {(v.eta_min != null || v.eta_max != null) && (
          <span className="listino-chip">{v.eta_min ?? 0}–{v.eta_max ?? 18} anni</span>
        )}
      </div>
      {Array.isArray(v.cosa_include) && v.cosa_include.length > 0 && (
        <ul className="listino-include">
          {v.cosa_include.map((c, i) => <li key={i}>{c}</li>)}
        </ul>
      )}
      {Array.isArray(v.opzioni) && v.opzioni.length > 0 && (
        <ul className="listino-opzioni">
          {v.opzioni.map((o, i) => (
            <li key={i}>{o.etichetta}{o.prezzo != null && o.prezzo !== '' ? ` — ${fmtEuro(o.prezzo)}` : ''}</li>
          ))}
        </ul>
      )}
      {v.note && <p className="listino-note">{v.note}</p>}
    </div>
  );
}

function DettaglioServizio() {
  const { providerSlug, schedaSlug, slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [servizio, setServizio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mainImage, setMainImage] = useState(0);
  const [lightbox, setLightbox] = useState(false);

  useEffect(() => {
    const loadServizio = async () => {
      try {
        // Nuovo formato: /servizio/<provider>/<scheda>. Legacy: /servizio/<slug-o-id>.
        const res = providerSlug && schedaSlug
          ? await api.get(`/servizi/by-slug/${providerSlug}/${schedaSlug}`)
          : await api.get(`/servizi/${idFromSlug(slug)}`);
        setServizio(res.data);
      } catch (err) {
        setError('Servizio non trovato');
        console.error('Error loading service:', err);
      } finally {
        setLoading(false);
      }
    };
    loadServizio();
  }, [providerSlug, schedaSlug, slug]);

  // SEO: se l'URL non è lo slug canonico (es. solo id, o nome cambiato),
  // sostituiscilo con quello "parlante" senza aggiungere voci alla cronologia.
  useEffect(() => {
    if (!servizio) return;
    const canonico = servizioPath(servizio);
    if (canonico !== '/cerca' && location.pathname !== canonico) {
      navigate(canonico, { replace: true });
    }
  }, [servizio, location.pathname, navigate]);

  // Lightbox: chiusura con Esc e navigazione con le frecce.
  useEffect(() => {
    if (!lightbox) return;
    const n = (servizio?.foto || []).length || 1;
    const onKey = (e) => {
      if (e.key === 'Escape') setLightbox(false);
      else if (e.key === 'ArrowRight') setMainImage((i) => (i + 1) % n);
      else if (e.key === 'ArrowLeft') setMainImage((i) => (i - 1 + n) % n);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox, servizio]);

  if (loading) {
    return (
      <div className="dettaglio-page loading">
        <div className="spinner" />
        Caricamento...
      </div>
    );
  }

  if (error || !servizio) {
    return (
      <div className="dettaglio-page error">
        <div className="error-container">
          <AlertCircle size={48} />
          <h2>{error || 'Servizio non trovato'}</h2>
          <p>Torna alla ricerca per trovare altri servizi</p>
          <Link to="/cerca" className="btn btn-primary">
            Torna alla Ricerca
          </Link>
        </div>
      </div>
    );
  }

  const foto = servizio.foto || [];
  const mainFoto = foto[mainImage]?.url || 'https://via.placeholder.com/600x400?text=Location';
  const prezzo = formatPrezzo(servizio);

  // Dati strutturati schema.org (Product + offerte di prezzo) per la SEO.
  const min = servizio.prezzo_min != null ? Number(servizio.prezzo_min) : null;
  const max = servizio.prezzo_max != null ? Number(servizio.prezzo_max) : null;
  let offers;
  if (min != null && max != null && max !== min) {
    offers = { '@type': 'AggregateOffer', priceCurrency: 'EUR', lowPrice: min, highPrice: max, availability: 'https://schema.org/InStock' };
  } else if (min != null || max != null) {
    offers = { '@type': 'Offer', priceCurrency: 'EUR', price: (min ?? max), availability: 'https://schema.org/InStock' };
  }
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: servizio.nome,
    description: (servizio.descrizione || '').slice(0, 300),
    ...(foto.length ? { image: foto.map((f) => f.url) } : {}),
    ...(servizio.nome_attivita ? { brand: { '@type': 'Brand', name: servizio.nome_attivita } } : {}),
    ...(offers ? { offers } : {}),
  };

  return (
    <div className="dettaglio-page">
      <Seo
        title={servizio.nome}
        description={(servizio.descrizione || '').slice(0, 155)}
        image={foto[0]?.url}
        type="article"
        canonical={servizioPath(servizio)}
        jsonLd={jsonLd}
      />
      {/* Breadcrumbs */}
      <div className="breadcrumbs">
        <Link to="/">Home</Link>
        <span> / </span>
        <Link to="/cerca">Ricerca</Link>
        <span> / </span>
        <span>{servizio.nome}</span>
      </div>

      <div className="dettaglio-container">
        {/* Left Column - Gallery & Details */}
        <div className="dettaglio-main">
          {/* Photo Gallery */}
          <div className="gallery-section">
            <div className="main-image">
              <img
                src={mainFoto}
                alt={servizio.nome}
                className={foto.length > 0 ? 'zoomable' : ''}
                onClick={() => foto.length > 0 && setLightbox(true)}
              />
              <div className="badge">
                {tipoLabel(servizio.tipo)}
              </div>
            </div>
            {foto.length > 1 && (
              <div className="thumbnails">
                {foto.map((f, idx) => (
                  <button
                    key={idx}
                    className={`thumbnail ${idx === mainImage ? 'active' : ''}`}
                    onClick={() => setMainImage(idx)}
                  >
                    <img src={f.url} alt={`Foto ${idx + 1}`} />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Service Info */}
          <div className="info-section">
            <div className="header-info">
              <h1>{servizio.nome}</h1>
              <div className="price-large">{prezzo}</div>
            </div>

            {/* Description */}
            <div className="description-section">
              <h3>Descrizione</h3>
              <p>{servizio.descrizione}</p>
            </div>

            {/* Elenco attività */}
            {Array.isArray(servizio.elenco_attivita) && servizio.elenco_attivita.length > 0 && (
              <div className="description-section">
                <h3>Cosa offriamo</h3>
                <ul className="elenco-attivita">
                  {servizio.elenco_attivita.map((voce, idx) => (
                    <li key={idx}>{voce}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Listino: pacchetti / prodotti e prezzi */}
            {Array.isArray(servizio.voci) && servizio.voci.length > 0 && (
              <div className="description-section listino-section">
                <h3>Listino</h3>
                <div className="listino-voci">
                  {servizio.voci.filter((v) => v.tipo !== 'extra').map((v) => (
                    <VoceListinoView key={v.id} v={v} />
                  ))}
                </div>
                {servizio.voci.some((v) => v.tipo === 'extra') && (
                  <div className="listino-extra-block">
                    <h4>Extra</h4>
                    <div className="listino-voci">
                      {servizio.voci.filter((v) => v.tipo === 'extra').map((v) => (
                        <VoceListinoView key={v.id} v={v} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Details Grid */}
            <div className="details-grid">
              <div className="detail-box">
                <Home size={24} />
                <div>
                  <strong>Ubicazione</strong>
                  <p>
                    {servizio.indoor_outdoor === 'indoor'
                      ? 'Al chiuso'
                      : servizio.indoor_outdoor === 'outdoor'
                      ? "All'aperto"
                      : 'Interno + esterno'}
                  </p>
                </div>
              </div>

              {servizio.citta && (
                <div className="detail-box">
                  <MapPin size={24} />
                  <div>
                    <strong>Località</strong>
                    <p>{[servizio.citta, servizio.regione].filter(Boolean).join(', ')}</p>
                  </div>
                </div>
              )}

              {servizio.categoria && (
                <div className="detail-box">
                  <div>
                    <strong>Sottocategoria</strong>
                    <p>{servizio.categoria}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Age Range */}
            {(servizio.eta_minima || servizio.eta_massima) && (
              <div className="age-section">
                <h3>Fascia d'Età Consigliata</h3>
                <p>
                  {servizio.eta_minima || '0'} - {servizio.eta_massima || '18'} anni
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Provider & Contact */}
        <div className="dettaglio-sidebar">
          {/* Provider Card */}
          <div className="provider-card">
            <div className="provider-header">
              <img
                src={servizio.avatar_url || '/default-avatar.svg'}
                alt={servizio.nome_attivita}
                className="provider-avatar"
                onError={(e) => { e.target.onerror = null; e.target.src = '/default-avatar.svg'; }}
              />
              <div className="provider-info">
                <h3>{servizio.nome_attivita}</h3>
                <p>{servizio.citta}</p>
              </div>
            </div>

            {servizio.descrizione_attivita && (
              <div className="provider-description">
                <p>{servizio.descrizione_attivita}</p>
              </div>
            )}

            <Link
              to={providerPath({ slug: servizio.provider_slug, id: servizio.provider_id })}
              className="btn btn-outline"
            >
              Visualizza Profilo
            </Link>
          </div>

          {/* Contact Form */}
          <ContactForm
            providerId={servizio.provider_id}
            servizioId={servizio.id}
            providerName={servizio.nome_attivita || 'il fornitore'}
            messaggiAttivi={servizio.messaggi_attivi}
          />
        </div>
      </div>

      {/* Lightbox: immagine a schermo intero */}
      {lightbox && foto.length > 0 && (
        <div className="lightbox-overlay" onClick={() => setLightbox(false)}>
          <button className="lightbox-close" aria-label="Chiudi" onClick={() => setLightbox(false)}>
            <X size={28} />
          </button>
          {foto.length > 1 && (
            <button
              className="lightbox-nav prev"
              aria-label="Precedente"
              onClick={(e) => { e.stopPropagation(); setMainImage((i) => (i - 1 + foto.length) % foto.length); }}
            >
              <ChevronLeft size={36} />
            </button>
          )}
          <img
            src={foto[mainImage]?.url}
            alt={servizio.nome}
            className="lightbox-image"
            onClick={(e) => e.stopPropagation()}
          />
          {foto.length > 1 && (
            <button
              className="lightbox-nav next"
              aria-label="Successiva"
              onClick={(e) => { e.stopPropagation(); setMainImage((i) => (i + 1) % foto.length); }}
            >
              <ChevronRight size={36} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default DettaglioServizio;
